import {
    BUSINESS_IMPACT_DRIVERS,
    type BusinessImpactDriver,
    type BusinessImpactDrivers,
    type BusinessImpactLevel,
    type ErrorCostLevel,
    type StepAssessment,
} from './automation-assessment-model';

// The backend's WorkTaskBusinessImpact computes the value the matrix plots; this mirrors it so the impact tile can
// explain that value and preview other levels. Both test suites pin the same examples, so a changed weight has to
// change on both sides.
const LEVEL_WEIGHT: Readonly<Record<BusinessImpactLevel, number>> = {
    CRITICAL: 1,
    MINOR: 0.1,
    NONE: 0,
    NOTICEABLE: 0.3,
    SIGNIFICANT: 0.6,
};

const DRIVER_WEIGHT: Readonly<Record<BusinessImpactDriver, number>> = {
    compliance: 0.85,
    cost: 0.8,
    peopleServed: 0.9,
    revenue: 1,
    risk: 1,
};

const ERROR_COST_WEIGHT: Readonly<Record<ErrorCostLevel, number>> = {
    COSTLY: 0.75,
    IRREVERSIBLE: 1,
    MACHINE_CHECKED: 0.2,
    MINOR: 0.25,
    NOTICEABLE: 0.5,
    TRIVIAL: 0,
};

/** The share of its hit a driver closes when another driver is hit at least as hard. */
const FURTHER_HIT_SHARE = 0.25;
/** The error cost that leaves the value where the drivers put it. */
const NEUTRAL_ERROR_COST = 0.5;
/** Scales the steps' shift to at most about five points halfway up the scale. */
const STEP_REACH = 0.4;

/** One driver the typical error hits, and the points of the whole it adds to the value. */
export type BusinessImpactHit = {
    readonly driver: BusinessImpactDriver;
    readonly isStrongest: boolean;
    readonly level: Exclude<BusinessImpactLevel, 'NONE'>;
    /** The share of the whole scale this hit closes, 0 to 1. */
    readonly points: number;
};

export type BusinessImpactBreakdown = {
    /** The drivers the typical error hits, strongest first. */
    readonly hits: readonly BusinessImpactHit[];
    /** How far the steps' error cost moves the value, -0.05 to 0.05. */
    readonly shift: number;
    /** The drivers the typical error does not reach, in driver order. */
    readonly unhit: readonly BusinessImpactDriver[];
    /** The business impact, 0 to 1. */
    readonly value: number;
};

/** The share of the gap a level on a driver closes; a critical hit closes all of it on any driver. */
const hitOf = (driver: BusinessImpactDriver, level: BusinessImpactLevel): number =>
    level === 'CRITICAL' ? 1 : LEVEL_WEIGHT[level] * DRIVER_WEIGHT[driver];

/** The steps' error costs weighed by their effort, 0 to 1; undefined without steps. */
export const errorCostOf = (steps: readonly StepAssessment[]): number | undefined => {
    const effort = steps.reduce((sum, step) => sum + step.normalizedEffortShare, 0);
    if (effort <= 0) return undefined;

    return (
        steps.reduce((sum, step) => sum + step.normalizedEffortShare * ERROR_COST_WEIGHT[step.errorCostLevel], 0) /
        effort
    );
};

/**
 * The business impact of the five driver levels and the steps' error cost, as the backend computes it. The strongest
 * hit closes its share of the gap to 1, every further hit a quarter of its share of what is left, so how hard a task
 * hits counts before how many drivers it touches. The error cost then moves the value a little, never at 0 or 1.
 */
export const businessImpactBreakdown = (
    drivers: BusinessImpactDrivers,
    errorCost: number | undefined,
): BusinessImpactBreakdown => {
    const ranked = BUSINESS_IMPACT_DRIVERS.map((driver) => ({
        driver,
        hit: hitOf(driver, drivers[driver]),
        level: drivers[driver],
    })).toSorted((first, second) => second.hit - first.hit);
    let gap = 1;
    const hits: BusinessImpactHit[] = [];
    ranked.forEach(({ driver, hit, level }, index) => {
        const points = gap * (index === 0 ? hit : FURTHER_HIT_SHARE * hit);
        gap -= points;
        if (level !== 'NONE') hits.push({ driver, isStrongest: index === 0, level, points });
    });
    const driven = 1 - gap;
    const shift = ((errorCost ?? NEUTRAL_ERROR_COST) - NEUTRAL_ERROR_COST) * STEP_REACH * driven * gap;

    return {
        hits,
        shift,
        unhit: BUSINESS_IMPACT_DRIVERS.filter((driver) => drivers[driver] === 'NONE'),
        value: driven + shift,
    };
};
